// Owner-gated sentry helpers for /eye: MediaPipe gestures + camera rPPG vitals (wellness estimate only).
const MP = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14";
const MODEL = "https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task";

export async function loadGestures() {
  const v = await new Function("u", "return import(u)")(`${MP}/vision_bundle.mjs`);
  const files = await v.FilesetResolver.forVisionTasks(`${MP}/wasm`);
  const mk = (delegate) => v.GestureRecognizer.createFromOptions(files, { baseOptions: { modelAssetPath: MODEL, delegate }, runningMode: "VIDEO", numHands: 1 });
  return mk("GPU").catch(() => mk("CPU"));
}

// Returns a gesture id from GESTURE map keys, or null.
export function readGesture(res) {
  const g = res?.gestures?.[0]?.[0];
  if (!g || g.score < 0.6) return null;
  if (g.categoryName === "Open_Palm") {
    const lm = res.landmarks?.[0];
    return lm && lm[12].y > lm[0].y ? "Open_Palm_Down" : "Open_Palm"; // fingertips below wrist = palm turned down
  }
  return ["Closed_Fist", "Victory", "Thumb_Up"].includes(g.categoryName) ? g.categoryName : null;
}

// 0.5s hold confirm: same gesture continuously for holdMs fires once.
export function makeHold(holdMs = 500) {
  let cur = null, since = 0, fired = false;
  return (g, now) => {
    if (g !== cur) { cur = g; since = now; fired = false; return null; }
    if (g && !fired && now - since >= holdMs) { fired = true; return g; }
    return null;
  };
}

const roiRects = (pts) => {
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  const fw = Math.max(...xs) - Math.min(...xs), fh = Math.max(...ys) - Math.min(...ys);
  const brow = Math.min(pts[19].y, pts[24].y);
  return [
    [pts[19].x, brow - fh * 0.22, pts[24].x - pts[19].x, fh * 0.15],                    // forehead
    [pts[2].x + fw * 0.08, pts[40].y + fh * 0.08, fw * 0.16, fh * 0.14],                  // left cheek
    [pts[14].x - fw * 0.24, pts[47].y + fh * 0.08, fw * 0.16, fh * 0.14],                 // right cheek
  ];
};

// Mean RGB over forehead + cheeks; mono=true when the camera is on IR night mode.
export function sampleRoi(video, pts, canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  let r = 0, g = 0, b = 0, n = 0;
  for (const [x, y, w, h] of roiRects(pts)) {
    if (w < 4 || h < 4) continue;
    ctx.drawImage(video, x, y, w, h, 0, 0, 12, 12);
    const d = ctx.getImageData(0, 0, 12, 12).data;
    for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n++; }
  }
  if (!n) return null;
  r /= n; g /= n; b /= n;
  return { r, g, b, mono: Math.abs(r - g) + Math.abs(g - b) < 4, dark: (r + g + b) / 3 < 35 };
}

const peakHz = (sig, fs, lo, hi) => {
  let best = 0, bf = 0, tot = 0;
  for (let f = lo; f <= hi; f += 0.01) {
    let re = 0, im = 0;
    sig.forEach((v, i) => { const a = 2 * Math.PI * f * i / fs; re += v * Math.cos(a); im -= v * Math.sin(a); });
    const p = re * re + im * im; tot += p;
    if (p > best) { best = p; bf = f; }
  }
  return { f: bf, q: tot ? best / tot * ((hi - lo) / 0.01) / 10 : 0 };
};

// 30s rolling rPPG (POS method) → {hr bpm, br breaths/min, quality}
export function makeVitals(windowS = 30) {
  let buf = [];
  return {
    push(t, s) { buf.push({ t, ...s }); buf = buf.filter((x) => t - x.t <= windowS * 1000); },
    reset() { buf = []; },
    span() { return buf.length > 1 ? (buf[buf.length - 1].t - buf[0].t) / 1000 : 0; },
    compute() {
      if (this.span() < 20 || buf.length < 150) return null;
      const fs = 15, t0 = buf[0].t, N = Math.floor(this.span() * fs), rs = [];
      for (let i = 0, j = 0; i < N; i++) {
        const tt = t0 + i * 1000 / fs;
        while (j < buf.length - 2 && buf[j + 1].t < tt) j++;
        rs.push(buf[j]);
      }
      const m = (k) => rs.reduce((s, x) => s + x[k], 0) / rs.length;
      const mr = m("r"), mg = m("g"), mb = m("b");
      const s1 = rs.map((x) => x.g / mg - x.b / mb), s2 = rs.map((x) => x.g / mg + x.b / mb - 2 * x.r / mr);
      const sd = (a) => Math.sqrt(a.reduce((s, v) => s + v * v, 0) / a.length) || 1;
      const alpha = sd(s1) / sd(s2);
      const pos = s1.map((v, i) => v + alpha * s2[i]);
      const mp = pos.reduce((a, b) => a + b, 0) / pos.length;
      const h = peakHz(pos.map((v) => v - mp), fs, 0.75, 3.0);
      const gm = rs.map((x) => x.g - mg);
      const br = peakHz(gm, fs, 0.15, 0.5);
      return { hr: Math.round(h.f * 60), br: Math.round(br.f * 60 * 10) / 10, quality: Math.min(1, h.q) };
    },
  };
}
